import { Alignment, Borders, Cell, FillPattern, Font, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { get, isNumber, set } from 'lodash';
import { ASSESSMENT_PERIOD_STATUSES, PROJECT_TYPES } from '@/constants';
import { AssessmentPeriodDTO, ProjectDTO } from '@/models';
import { convertNumberToFourDigitsString, formatDateToDisplay, getDisplayQuarterByDate } from '@/helpers';
import { getUserDisplayInfo } from './user';
import { STYLES, NA } from './project-exporting-config';
import { getActualEffort } from './project-drops';
import { addTableHeaderRow } from './project-exporting-helpers';

type Style = {
  fill?: FillPattern;
  border?: Partial<Borders>;
  font?: Partial<Font>;
  alignment?: Partial<Alignment>;
};
type callbackFn = (cell: Cell, index: number) => void;

const EMPTY = '';

export default class AssessmentPeriodExport {
  private workbook = new Workbook();
  private ws: Worksheet;
  private projects: ProjectDTO[] = [];
  private assessmentPeriod: AssessmentPeriodDTO;
  private totalAssessmentPeriodDrops: number | null = 0;
  private columnWidths = [8, 12, 30, 30, 30, 30, 12, 12, 12, 15, 12];

  constructor(assessmentPeriod: AssessmentPeriodDTO, projects: ProjectDTO[], totalAssessmentPeriodDrops?: number) {
    const sheetName = AssessmentPeriodExport.buildAssessmentSheetName(assessmentPeriod.name);
    const existWorkSheet = !!this.workbook.worksheets.find(ws => ws && ws.name === sheetName);
    if (existWorkSheet) {
      throw new Error(`Worksheet is already exist!`);
    } else {
      this.assessmentPeriod = assessmentPeriod;
      this.projects = projects;
      this.totalAssessmentPeriodDrops = totalAssessmentPeriodDrops || null;
      this.ws = this.workbook.addWorksheet(sheetName);
    }
  }

  static buildAssessmentSheetName = (assessmentName: string): string => {
    return (assessmentName || '').replace(/[/\\*'"?[\]:;]+/g, '');
  };

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  get exportFileName(): string {
    return `HIVE_Assessment_Result_${this.assessmentPeriod.name}.xlsx`;
  }

  addRow(data: any[], style?: Style, callback?: callbackFn): Row {
    const row = this.ws.addRow(data);
    row.eachCell((cell, index) => {
      if (cell.value === EMPTY) {
        return;
      }
      if (typeof cell.value === 'number') {
        cell.alignment = STYLES.middleRightWrappedAlignment;
      } else {
        cell.alignment = STYLES.middleLeftWrappedAlignment;
      }
      if (cell.value === NA) {
        cell.font = STYLES.naFont;
        cell.alignment = STYLES.middleRightWrappedAlignment;
      }
      this.addStyleToCell(cell, style);
      if (callback) {
        callback(cell, index);
      }
    });
    return row;
  }

  interleaveRowStyle(data: any[], index: number, callbackFn?: callbackFn): Row {
    return index % 2 === 0
      ? this.addRow(data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(data, {}, callbackFn);
  }

  buildAssessmentInformation(): any[] {
    const { name, createdAt, createdBy, startDate, endDate } = this.assessmentPeriod;
    const result = [
      ['Name', name],
      ['Create by', getUserDisplayInfo(createdBy)],
      ['Create at', formatDateToDisplay(createdAt)],
      ['Start date', formatDateToDisplay(startDate)],
      ['End date', formatDateToDisplay(endDate)],
      ['Total project', this.projects.length],
      ['Total pollens', this.totalAssessmentPeriodDrops || NA]
    ];
    return result.map(assessmentInformation => [...Array(1).fill(EMPTY), ...assessmentInformation]);
  }

  mergeCells(row: Row, from: number, to: number): void {
    this.ws.mergeCells(`${row.getCell(from).address}:${row.getCell(to).address}`);
  }

  addTitleRow(title: string, mergeLength: number): void {
    const row = this.ws.addRow([title, ...Array(mergeLength - 2).fill('')]);
    this.mergeCells(row, 1, mergeLength);
    const address = `${row.getCell(1).address}`;
    this.ws.getCell(address).font = STYLES.h1Font;
  }

  addTableHeaderRow(header: string[]): void {
    const row = this.ws.addRow(header);
    row.eachCell(cell => {
      cell.font = STYLES.boldFont;
      cell.fill = STYLES.primaryTealFill;
    });
  }

  addEmptyRow(): void {
    this.ws.addRow([]);
  }

  addAssessmentInformationToWorkSheet(): void {
    const rows = this.buildAssessmentInformation();
    this.addTitleRow(`Information`, rows[0].length);
    this.addEmptyRow();

    rows.forEach((rowData, rowIndex) => {
      if (rowIndex === 0) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderTop, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      if (rowIndex === rows.length - 1) {
        this.addRow(rowData, { border: STYLES.primaryTealBorderBottom, fill: STYLES.lightTealFill }, (cell, index) => {
          if (index === 2) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(rowData, rowIndex, (cell, index) => {
        if (index === 2) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  buildProjectInformation(project: ProjectDTO, index: number): any[] {
    const { id, name, projectAssessments, projectType, sponsor, manager, projectStatus } = project || {};
    const { DONE } = ASSESSMENT_PERIOD_STATUSES;
    const status = this.assessmentPeriod.status === DONE.label ? DONE.label : projectStatus;
    const {
      key = NA,
      plannedEffort = 0,
      userEngagements = [],
      createdAt
    } = projectAssessments?.find(pa => pa.assessmentPeriodId === this.assessmentPeriod.id) || {};
    const hours = getActualEffort(projectType, plannedEffort, userEngagements);
    const projectDrops = this.getProjectDrop(project);
    return [
      index + 1,
      convertNumberToFourDigitsString(id),
      name,
      projectType,
      getUserDisplayInfo(sponsor),
      getUserDisplayInfo(manager),
      isNumber(hours) ? hours : NA,
      isNumber(key) ? key : NA,
      isNumber(projectDrops) ? projectDrops : NA,
      status,
      getDisplayQuarterByDate(this.assessmentPeriod.startDate || '')
    ];
  }

  addProjectInformationToWorksheet(): void {
    const header = [
      { title: 'No.' },
      { title: 'Project ID' },
      { title: 'Project name' },
      { title: 'Project Type' },
      { title: 'Sponsor' },
      { title: 'Project Manager' },
      { title: 'Hours', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Pollens', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Project Pollens' },
      { title: 'Evaluation Status' },
      { title: 'Period', style: { fill: STYLES.lightOrangeFill } }
    ];
    this.addTitleRow(`Project list`, header.length);
    this.addEmptyRow();
    addTableHeaderRow(this.ws, header);
    const boldColumnIndex = 9;
    const projectRows = [...this.projects]
      .sort((preProject: ProjectDTO, currentProject: ProjectDTO) => {
        return ('' + preProject.name).localeCompare(currentProject.name);
      })
      .map((project, index) => this.buildProjectInformation(project, index));

    projectRows.forEach((projectRowData, projectIndex) => {
      if (projectIndex === projectRows.length - 1) {
        this.addRow(projectRowData, { border: STYLES.primaryTealBorderBottom }, (cell, index) => {
          if (index === boldColumnIndex) {
            cell.font = STYLES.boldFont;
          }
        });
        return;
      }
      this.interleaveRowStyle(projectRowData, projectIndex, (cell, index) => {
        if (index === boldColumnIndex) {
          cell.font = STYLES.boldFont;
        }
      });
    });
  }

  setColumnWidth(): void {
    this.ws.columns.forEach((column, index) => {
      column.width = this.columnWidths[index];
    });
  }

  getProjectDrop(project: ProjectDTO): number | null {
    const assessmentPeriodId = this.assessmentPeriod.id;
    const currentProjectAssessment = project.projectAssessments.find(
      projectAssessment => projectAssessment.assessmentPeriodId === assessmentPeriodId
    );
    if (currentProjectAssessment) {
      if (project.projectType === PROJECT_TYPES.INDIVIDUAL_TRACKING) {
        return (
          currentProjectAssessment.userEngagements.reduce((prev, userEngagement) => {
            return userEngagement.individualDrops ? userEngagement.individualDrops + prev : prev;
          }, 0) || null
        );
      } else {
        return currentProjectAssessment.finalDrops;
      }
    } else {
      return null;
    }
  }

  async generateXLSXFile(): Promise<void> {
    this.addAssessmentInformationToWorkSheet();
    this.addEmptyRow();
    this.addEmptyRow();
    this.addProjectInformationToWorksheet();
    this.setColumnWidth();

    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }
}
